import {
  CheckoutSchema,
  ProductTypeEnum,
  SourceStatusEnum,
  ActionStatusEnum,
  SalesChannelEnum,
  ProductDataSchema,
  PaymentMethodEnum,
  type DailySummary,
  BusinessActionEnum,
  BusinessDeviceType,
  type ProductDataType,
  BusinessProductAreaV2,
  type CheckoutDataType,
  PaymentTransactionTypeV4,
  DeviceSyncResponseSchema,
  BusinessAccessPolicyModeV2,
  ApplicationDeviceContextSchema,
} from '@am3nd-org/pointcraft-types';

import { DEFAULT_PRODUCT_URL } from 'src/config-global';
import type {
  SalesV4ListItem,
  SaleDetailV4Response,
} from 'src/version-4/pages/transactions/types';

import { OFFLINE_DEMO_TIME_ZONE } from './types';

export const OFFLINE_DEMO_FIXTURE_VERSION = 2;
export const OFFLINE_DEMO_FIXTURE_VERSION_KEY = 'offline_demo_fixture_version';
export const OFFLINE_DEMO_LEDGER_KEY = 'offline_demo_sales_ledger';

export const DEMO_BUSINESS_ID = '11111111-1111-4111-8111-111111111111';
export const DEMO_LOCATION_ID = '22222222-2222-4222-8222-222222222222';
export const DEMO_DEVICE_ID = '33333333-3333-4333-8333-333333333333';
export const DEMO_USER_ID = '44444444-4444-4444-8444-444444444444';
export const DEMO_ROLE_ID = '55555555-5555-4555-8555-555555555555';

function businessDate(offsetDays = 0): string {
  const nowParts = new Intl.DateTimeFormat('en-US', {
    timeZone: OFFLINE_DEMO_TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date());
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(nowParts.find((part) => part.type === type)?.value ?? 0);
  return new Date(
    Date.UTC(value('year'), value('month') - 1, value('day') + offsetDays, 18)
  )
    .toISOString()
    .slice(0, 10);
}

export const DEMO_TODAY = () => businessDate(0);
export const DEMO_YESTERDAY = () => businessDate(-1);

const productDefinitions = [
  {
    id: '10000000-0000-4000-8000-000000000001',
    identifier: '900000000001',
    name: 'Classic Smash Burger',
    description: 'Two smashed beef patties, American cheese, pickles, and house sauce.',
    price: 12.5,
    category: 'Entrées',
    stock: 48,
    modifiers: [
      ['Add bacon', 2.25],
      ['Extra patty', 3.5],
    ],
  },
  {
    id: '10000000-0000-4000-8000-000000000002',
    identifier: '900000000002',
    name: 'Grilled Chicken Wrap',
    description: 'Seasoned chicken, greens, tomato, cheddar, and ranch.',
    price: 10.75,
    category: 'Entrées',
    stock: 35,
    modifiers: [['Add avocado', 1.75]],
  },
  {
    id: '10000000-0000-4000-8000-000000000003',
    identifier: '900000000003',
    name: 'Roasted Veggie Bowl',
    description: 'Quinoa, seasonal vegetables, chickpeas, and lemon tahini.',
    price: 11.25,
    category: 'Entrées',
    stock: 28,
    modifiers: [['Add chicken', 3.25]],
  },
  {
    id: '10000000-0000-4000-8000-000000000004',
    identifier: '900000000004',
    name: 'Sea Salt Fries',
    description: 'Crispy skin-on fries with sea salt.',
    price: 4.25,
    category: 'Sides',
    stock: 62,
    modifiers: [['Cheese sauce', 1.25]],
  },
  {
    id: '10000000-0000-4000-8000-000000000005',
    identifier: '900000000005',
    name: 'Crispy Wings',
    description: 'Six crispy wings with a choice of sauce.',
    price: 9.5,
    category: 'Sides',
    stock: 31,
    modifiers: [
      ['Buffalo sauce', 0],
      ['BBQ sauce', 0],
    ],
  },
  {
    id: '10000000-0000-4000-8000-000000000006',
    identifier: '900000000006',
    name: 'House Lemonade',
    description: 'Fresh lemon, cane sugar, and filtered water.',
    price: 3.5,
    category: 'Drinks',
    stock: 80,
    modifiers: [['Large', 1]],
  },
  {
    id: '10000000-0000-4000-8000-000000000007',
    identifier: '900000000007',
    name: 'Cold Brew Coffee',
    description: 'Slow-steeped coffee served over ice.',
    price: 4.75,
    category: 'Drinks',
    stock: 44,
    modifiers: [['Oat milk', 0.75]],
  },
  {
    id: '10000000-0000-4000-8000-000000000008',
    identifier: '900000000008',
    name: 'Chocolate Brownie',
    description: 'Fudgy chocolate brownie with a crisp top.',
    price: 4.5,
    category: 'Desserts',
    stock: 24,
    modifiers: [['Vanilla ice cream', 2]],
  },
] as const;

export const DEMO_PRODUCTS: ProductDataType[] = productDefinitions.map((definition, index) =>
  ProductDataSchema.parse({
    id: definition.id,
    identifier: definition.identifier,
    name: definition.name,
    description: definition.description,
    cost: Number((definition.price * 0.36).toFixed(2)),
    price: definition.price,
    singleUse: false,
    sold: false,
    trackStockLevels: true,
    currentStockLevel: definition.stock,
    reorderStockLevel: 10,
    type: ProductTypeEnum.PREPARED_FOOD,
    defaultImageUrl: DEFAULT_PRODUCT_URL,
    itemCategory: definition.category,
    createdAt: '2026-01-01T12:00:00.000Z',
    updatedAt: '2026-08-01T12:00:00.000Z',
    deletedAt: null,
    attributes: {
      extraImages: [],
      isTaxable: true,
      isEnabled: true,
      ebtEligible: false,
      tags: ['OFFLINE_DEMO'],
      preparedFood: { ingredients: [] },
      posFavorite: {
        isFavorite: index < 4,
        sortOrder: index,
        color: ['#8D6E63', '#558B2F', '#00838F', '#F57F17'][index % 4],
        displayName: definition.name,
      },
      modifiers: {
        groups: definition.modifiers.length
          ? [
              {
                id: `20000000-0000-4000-8000-${String(index + 1).padStart(12, '0')}`,
                name: 'Options',
                required: false,
                min: 0,
                max: 2,
                sortOrder: 0,
                modifiers: definition.modifiers.map(([name, priceDelta], modifierIndex) => ({
                  id: `30000000-0000-4000-8000-${String(index * 10 + modifierIndex + 1).padStart(12, '0')}`,
                  name,
                  priceDelta,
                  costDelta: 0,
                  linkedProductId: null,
                  depletesInventory: false,
                  isDefault: false,
                })),
              },
            ]
          : [],
      },
    },
  })
);

export const DEMO_DEVICE_SYNC = DeviceSyncResponseSchema.parse({
  businessData: {
    id: DEMO_BUSINESS_ID,
    accountNumber: 'DEMO-0001',
    name: 'PointCraft Demo Kitchen',
    isSuperAdmin: false,
    businessInfo: {
      address: {
        street1: '3055 Old Hwy 8',
        street2: 'Suite 109',
        city: 'Minneapolis',
        state: 'MN',
        postalCode: '55418',
        country: 'US',
      },
      phone: '(612) 555-0148',
      email: 'owner@pointcraft-demo.local',
      website: 'https://pointcraft.io',
      logoUrl: null,
    },
    financialDefaults: {
      currency: 'USD',
      taxRate: 0.08525,
      limits: {
        web: { paymentLinks: 0 },
        cardPayments: { dailyMaxInCents: 0, perTransactionInCents: 0 },
      },
      processingConfig: {
        rate: 0.029,
        flatFeeExemptions: [],
        flatFeeRules: [{ min: 0, max: 1000000, flatFee: 0.3 }],
        customerSplits: { rate: 0, fixed: 0 },
      },
      canCarryBalance: false,
      rollingBalanceLimit: 0,
      acceptTips: true,
    },
    configurations: {
      offlineModeEnabled: false,
      businessType: 'RESTAURANT',
      permissions: [],
      features: [],
      mccType: '5812',
      canProcessOrders: false,
      viewSimpleReports: false,
      timeZone: OFFLINE_DEMO_TIME_ZONE,
      quickBooksSyncEnabled: false,
      quickBooksSyncType: 'NIGHTLY',
      saleReceiptConfig: { footerText: 'Offline demo — no real payment was processed.' },
      preferences: {
        displayBiggerTextUi: false,
        autoAcceptOrders: false,
        autoCompleteOrders: false,
        autoPrintReceipt: false,
        restaurantPosPreferences: { defaultTab: 'ALL', holdOrdersEnabled: true },
      },
    },
    access_v2: {
      businessAccess: {
        productAreas: [
          BusinessProductAreaV2.POINT_OF_SALE_RESTAURANT,
          BusinessProductAreaV2.REPORTS,
          BusinessProductAreaV2.TRANSACTIONS_V4,
          BusinessProductAreaV2.SETTINGS,
        ],
        actions: [BusinessActionEnum.REPORT_VIEW, BusinessActionEnum.SETTINGS_VIEW],
        paymentMethods: [],
        policy: {
          mode: BusinessAccessPolicyModeV2.BUSINESS_ONLY,
          requireUserForSensitiveActions: false,
          sensitiveActions: [],
        },
        settings: {
          ui: { displayBiggerText: false },
          checkout: {
            allowSplitPayments: false,
            tipSelectionEnabled: true,
            taxesEnabledDefault: true,
            customerPaysFeesDefault: false,
            displayLineItemsOnTerminal: false,
            holdSalesEnabled: true,
          },
          orders: {
            enabled: false,
            autoAcceptOrders: false,
            autoCompleteOrders: false,
            tableServiceEnabled: false,
            holdOrdersEnabled: true,
            orderNumberStart: 1,
          },
          printing: {
            enabled: false,
            autoPrintReceipt: false,
            printDoubleKdsReceipt: false,
            footerText: 'Offline demo — no real payment was processed.',
          },
          system: {
            offlineModeEnabled: false,
            timeZone: OFFLINE_DEMO_TIME_ZONE,
            requireOperatorAuthentication: false,
            operatorAuthenticationDurationMinutes: 15,
          },
          reporting: { simpleReportsEnabled: false },
          integrations: { manualTransactionSyncEnabled: false },
          catalog: { itemCategories: ['Entrées', 'Sides', 'Drinks', 'Desserts'] },
        },
        tags: ['STORE_REPORT'],
      },
      roleDefinitions: [],
    },
    system_pin: '0000',
  },
  userData: [
    {
      id: DEMO_USER_ID,
      businessId: DEMO_BUSINESS_ID,
      roleId: DEMO_ROLE_ID,
      profile: {
        name: 'Alex Demo Owner',
        email: 'owner@pointcraft-demo.local',
        phone: '(612) 555-0101',
      },
      isActive: true,
      isDefault: true,
    },
  ],
  terminals: [],
  businessDevices: [
    {
      id: DEMO_DEVICE_ID,
      businessId: DEMO_BUSINESS_ID,
      businessLocationId: DEMO_LOCATION_ID,
      terminalId: null,
      deviceName: 'Demo Front Counter',
      deviceType: BusinessDeviceType.POS_WEB_DEVICE,
      status: 'ACTIVE',
      serialNumber: 'OFFLINE-DEMO-01',
      lastPairedAt: '2026-01-01T12:00:00.000Z',
      createdAt: '2026-01-01T12:00:00.000Z',
      updatedAt: '2026-08-01T12:00:00.000Z',
    },
  ],
  providerDetails: [],
  recurringPlans: [],
});

export const DEMO_DEVICE_CONTEXT = ApplicationDeviceContextSchema.parse({
  accountNumber: 'DEMO-0001',
  businessId: DEMO_BUSINESS_ID,
  businessDeviceId: DEMO_DEVICE_ID,
  businessLocationId: DEMO_LOCATION_ID,
  terminalId: null,
  deviceFingerprint: 'offline-demo-browser',
  publicKeyFingerprint: 'offline-demo-public-key',
  deviceName: 'Demo Front Counter',
  deviceType: BusinessDeviceType.POS_WEB_DEVICE,
  environment: 'offline-demo',
  lastResolvedAt: '2026-08-01T12:00:00.000Z',
});

export type OfflineDemoSaleFixture = {
  listItem: SalesV4ListItem;
  detail: SaleDetailV4Response;
};

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function createSaleFixture(
  index: number,
  date: string,
  hour: number,
  paymentMethod: PaymentMethodEnum,
  productIndexes: number[]
): OfflineDemoSaleFixture {
  const id = `demo-sale-${date}-${String(index + 1).padStart(3, '0')}`;
  const createdAt = `${date}T${String(hour).padStart(2, '0')}:${index % 2 ? '35' : '10'}:00.000Z`;
  const products = productIndexes.map((productIndex) => DEMO_PRODUCTS[productIndex]);
  const subtotal = roundMoney(products.reduce((sum, product) => sum + product.price, 0));
  const tax = roundMoney(subtotal * 0.08525);
  const tip = index % 4 === 0 ? 2 : 0;
  const total = roundMoney(subtotal + tax + tip);
  const paymentId = `demo-payment-${date}-${index + 1}`;
  const shortIdentifier = `D${date.replaceAll('-', '').slice(2)}${String(index + 1).padStart(2, '0')}`;

  const checkoutData: CheckoutDataType = CheckoutSchema.parse({
    id,
    shortIdentifier,
    isInvoice: false,
    isPosSale: true,
    isMbsSale: false,
    salesChannel: SalesChannelEnum.POS,
    currentInput: null,
    businessLocationId: DEMO_LOCATION_ID,
    isCreated: true,
    isCompleted: true,
    isReadyToPay: true,
    details: {
      date: createdAt,
      dueDate: null,
      terms: '',
      displayLogo: true,
      lateFeesEnabled: false,
      enableTaxes: true,
      clientPaysFees: false,
      customerNotes: null,
      orderNumber: null,
      requiresOrderProcessing: false,
      syncedToQuickBooks: false,
    },
    status: SourceStatusEnum.PAID,
    financialInfo: {
      total,
      cost: roundMoney(products.reduce((sum, product) => sum + product.cost, 0)),
      subTotal: subtotal,
      balanceDue: 0,
      payment: {
        cash: paymentMethod === PaymentMethodEnum.CASH ? total : 0,
        card: paymentMethod === PaymentMethodEnum.CARD ? total : 0,
        ebt: 0,
        totalAmount: total,
        transactions: [
          {
            id: paymentId,
            type: paymentMethod,
            amount: total,
            merchantNetAmount: total,
            date: createdAt,
            status: ActionStatusEnum.SUCCESS,
            statusV4: 'SUCCEEDED',
            transactionType: PaymentTransactionTypeV4.PAYMENT,
            parentTransactionId: null,
          },
        ],
      },
      processingFees: 0,
      merchantProcessingFees: 0,
      shippingCharges: 0,
      adjustment: 0,
      taxes: tax,
      tip,
      discount: 0,
      copyOfProcessingConfig: {
        rate: 0.029,
        flatFeeRules: [{ min: 0, max: 1000000, flatFee: 0.3 }],
        flatFeeExemptions: [],
        customerSplits: { rate: 0, fixed: 0 },
      },
    },
    customerInfo:
      index % 3 === 0
        ? {
            id: `demo-customer-${index + 1}`,
            name: ['Avery Johnson', 'Samira Ali', 'Jordan Lee', 'Mateo Garcia'][index % 4],
            email: `guest${index + 1}@example.demo`,
            phone: `(612) 555-${String(1000 + index).slice(-4)}`,
            debtTracker: null,
            paymentMethods: [],
          }
        : null,
    employee: {
      name: 'Alex Demo Owner',
      userId: DEMO_USER_ID,
      email: 'owner@pointcraft-demo.local',
      phone: '(612) 555-0101',
      isActive: true,
      isDefault: true,
      features: [],
      permissions: [],
      configurations: { virtualKeyboard: false },
      role: 'OWNER',
      roleId: DEMO_ROLE_ID,
    },
    items: products.map((product, productIndex) => ({
      id: `${id}-item-${productIndex + 1}`,
      identifier: product.identifier,
      name: product.name,
      price: product.price,
      cost: product.cost,
      quantity: 1,
      linkedProduct: product,
      linkedMobileService: null,
      productType: product.type,
      metadata: {},
    })),
  });

  let cardBrand: string | null = null;
  if (paymentMethod === PaymentMethodEnum.CARD) {
    cardBrand = index % 2 ? 'Mastercard' : 'Visa';
  }

  const listPayment = {
    id: paymentId,
    transactionId: paymentId,
    parentTransactionId: null,
    providerTransactionId: null,
    provider: paymentMethod === PaymentMethodEnum.CARD ? 'DEMO_CARD' : null,
    method: paymentMethod,
    status: 'SUCCESS',
    statusV4: 'SUCCEEDED',
    amount: total,
    approvedAmount: total,
    tipAmount: tip,
    cardLast4: paymentMethod === PaymentMethodEnum.CARD ? String(4100 + index).slice(-4) : null,
    cardBrand,
    entryMode: paymentMethod === PaymentMethodEnum.CARD ? 'CONTACTLESS' : null,
    createdAt,
    updatedAt: createdAt,
  };

  return {
    listItem: {
      id,
      shortIdentifier,
      businessId: DEMO_BUSINESS_ID,
      businessLocationId: DEMO_LOCATION_ID,
      status: SourceStatusEnum.PAID,
      salesChannel: SalesChannelEnum.POS,
      isPosSale: true,
      isInvoice: false,
      orderNumber: null,
      createdAt,
      updatedAt: createdAt,
      modifiedAt: createdAt,
      deletedAt: null,
      completedAt: createdAt,
      totals: { subtotal, tax, fees: 0, tip, total, balanceDue: 0 },
      customer: checkoutData.customerInfo
        ? {
            id: checkoutData.customerInfo.id,
            name: checkoutData.customerInfo.name,
            phone: checkoutData.customerInfo.phone,
          }
        : null,
      payments: [listPayment],
      cancellationRequest: null,
    },
    detail: {
      checkoutData,
      payments: [
        {
          transactionId: paymentId,
          transactionType: PaymentTransactionTypeV4.PAYMENT,
          parentTransactionId: null,
          method: paymentMethod,
          status: 'SUCCESS',
          statusV4: 'SUCCEEDED',
          amount: { cents: Math.round(total * 100), dollars: total },
          refundAmount: { cents: 0, dollars: 0 },
          card: {
            last4: listPayment.cardLast4,
            brand: listPayment.cardBrand,
          },
          createdAt,
        },
      ],
      refunds: [],
      cancellationRequest: null,
    },
  };
}

export function createDemoSalesLedger(): OfflineDemoSaleFixture[] {
  const today = DEMO_TODAY();
  const yesterday = DEMO_YESTERDAY();
  return [
    createSaleFixture(0, today, 15, PaymentMethodEnum.CARD, [0, 3]),
    createSaleFixture(1, today, 16, PaymentMethodEnum.CASH, [1, 5]),
    createSaleFixture(2, today, 17, PaymentMethodEnum.CARD, [2, 7]),
    createSaleFixture(3, today, 19, PaymentMethodEnum.CASH, [4, 3]),
    createSaleFixture(4, today, 21, PaymentMethodEnum.CARD, [0, 6]),
    createSaleFixture(5, today, 23, PaymentMethodEnum.CASH, [1, 7]),
    createSaleFixture(6, yesterday, 15, PaymentMethodEnum.CASH, [0, 5]),
    createSaleFixture(7, yesterday, 16, PaymentMethodEnum.CARD, [2, 3]),
    createSaleFixture(8, yesterday, 18, PaymentMethodEnum.CARD, [4, 6]),
    createSaleFixture(9, yesterday, 19, PaymentMethodEnum.CASH, [1, 3]),
    createSaleFixture(10, yesterday, 21, PaymentMethodEnum.CARD, [0, 7]),
    createSaleFixture(11, yesterday, 23, PaymentMethodEnum.CASH, [2, 5]),
  ].sort((a, b) => b.listItem.createdAt.localeCompare(a.listItem.createdAt));
}

export function deriveDailySummaries(
  ledger: readonly OfflineDemoSaleFixture[]
): DailySummary[] {
  const grouped = new Map<string, OfflineDemoSaleFixture[]>();
  ledger.forEach((sale) => {
    const key = sale.listItem.createdAt.slice(0, 10);
    grouped.set(key, [...(grouped.get(key) ?? []), sale]);
  });

  return Array.from(grouped.entries())
    .map(([date, sales]) => {
      const cents = (value: number) => Math.round(value * 100);
      const subtotal = sales.reduce((sum, sale) => sum + (sale.listItem.totals.subtotal ?? 0), 0);
      const taxes = sales.reduce((sum, sale) => sum + (sale.listItem.totals.tax ?? 0), 0);
      const tips = sales.reduce((sum, sale) => sum + (sale.listItem.totals.tip ?? 0), 0);
      const cash = sales
        .filter((sale) => sale.listItem.payments[0]?.method === PaymentMethodEnum.CASH)
        .reduce((sum, sale) => sum + sale.listItem.totals.total, 0);
      const card = sales
        .filter((sale) => sale.listItem.payments[0]?.method === PaymentMethodEnum.CARD)
        .reduce((sum, sale) => sum + sale.listItem.totals.total, 0);

      return {
        id: `demo-summary-${date}`,
        businessId: DEMO_BUSINESS_ID,
        date: new Date(`${date}T12:00:00.000Z`),
        timeZone: OFFLINE_DEMO_TIME_ZONE,
        gross_sales_cents: cents(subtotal),
        net_sales_cents: cents(subtotal),
        net_revenue_cents: cents(subtotal + tips),
        cash_sales_cents: cents(cash),
        card_sales_cents: cents(card),
        ebt_sales_cents: 0,
        debt_amount_cents: 0,
        taxes_cents: cents(taxes),
        non_card_sales_tax_cents: 0,
        card_sales_tax_cents: cents(taxes),
        fees_cents: 0,
        tip_cents: cents(tips),
        refund_amount_cents: 0,
        number_of_sales: sales.length,
        payoutData: { beforeCutoff: 0, afterCutoff: 0 },
        paymentLinksCreated: 0,
        syncedToQuickBooks: null,
        metaData: null,
      } satisfies DailySummary;
    })
    .sort((a, b) => b.date.getTime() - a.date.getTime());
}
